/**
 * Dashboard — project list view.
 *
 * Shows all Thread projects accessible to the current user.
 * Entry point after sign-in.
 */

import { useEffect, useCallback, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  Button,
  Dialog,
  Flex,
  TextField,
} from "@radix-ui/themes";

import { supabase } from "../lib/supabase";
import {
  fetchProjects,
  createProject,
  deleteProject,
  updateProject,
} from "../api/projects";
import { ThreadLogo } from "../components/ThreadLogo";
import { ThemeToggle } from "../components/toolbar/ThemeToggle";
import { useCampaignListQuery } from "../features/adaptive-sampling/api";
import "./Dashboard.css";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  if (s < 604800) return `${Math.floor(s / 86400)} days ago`;
  return `${Math.floor(s / 604800)} weeks ago`;
}

function CampaignStatusPill({ status }: { status: string }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    running: { bg: "rgba(64, 224, 160, 0.14)", fg: "#40e0a0" },
    paused: { bg: "rgba(255, 180, 70, 0.14)", fg: "#ffb446" },
    done: { bg: "rgba(120, 170, 255, 0.14)", fg: "#78aaff" },
    failed: { bg: "rgba(255, 90, 90, 0.14)", fg: "#ff5a5a" },
    configuring: { bg: "rgba(140, 140, 150, 0.14)", fg: "#9a9aa0" },
  };
  const p = palette[status] ?? palette.configuring!;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 8px",
        borderRadius: 10,
        background: p.bg,
        color: p.fg,
        fontSize: 11,
        fontWeight: 500,
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );
}

function CampaignList() {
  const navigate = useNavigate();
  const { data: campaigns, isLoading } = useCampaignListQuery();

  if (isLoading) return null;
  if (!campaigns || campaigns.length === 0) return null;

  return (
    <div style={{ marginTop: 8, marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <h2
          style={{
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: 0.3,
            color: "#a0a0a8",
            textTransform: "uppercase",
            margin: 0,
          }}
        >
          Adaptive sampling campaigns
        </h2>
        <Link
          to="/campaigns/new"
          style={{ color: "#ff4080", fontSize: 12, textDecoration: "none" }}
        >
          + New campaign
        </Link>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {campaigns.map((c) => (
          <div
            key={c.id}
            className="project-card"
            onClick={() => navigate(`/campaigns/${c.id}`)}
            style={{ cursor: "pointer" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  className="project-icon"
                  style={{
                    background: "rgba(255, 64, 128, 0.12)",
                    border: "1px solid rgba(255, 64, 128, 0.25)",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ff4080"
                    strokeWidth="1.5"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4" />
                  </svg>
                </div>
                <div>
                  <div
                    style={{ fontSize: 14, fontWeight: 500, color: "#e6e6e8" }}
                  >
                    {c.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#8b8b93",
                      marginTop: 2,
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <CampaignStatusPill status={c.status} />
                    <span>
                      {c.pointsComplete} / {c.budget} runs · iter {c.iteration}
                      {c.oracleEnabled && c.oracleDatasetId
                        ? ` · oracle: ${c.oracleDatasetId}`
                        : ""}
                    </span>
                    <span>· {timeAgo(c.updatedAt)}</span>
                  </div>
                </div>
              </div>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#52525b"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: fetchProjects,
  });

  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [editName, setEditName] = useState("");
  const [deletingProject, setDeletingProject] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const deleteMut = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setDeletingProject(null);
    },
  });

  const createMut = useMutation({
    mutationFn: (name: string) => createProject(name),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate(`/project/${project.id}`);
    },
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      updateProject(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setEditingProject(null);
    },
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    sessionStorage.removeItem("flow360_access_token");
    sessionStorage.removeItem("flow360_refresh_token");
    sessionStorage.removeItem("flow360_cognito_sub");
    sessionStorage.removeItem("flow360_user_email");
    window.location.reload();
  };

  const handleCreateProject = useCallback(() => {
    if (!createMut.isPending) createMut.mutate("Untitled Project");
  }, [createMut]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "n" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        handleCreateProject();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleCreateProject]);

  return (
    <div className="dashboard">
      <div className="dashboard-orb-1" />
      <div className="dashboard-orb-2" />

      {/* Navigation */}
      <nav className="dashboard-nav">
        <ThreadLogo height={28} />

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <a
            href="https://flight-sciences.dev-simulation.cloud/thread/docs/"
            target="_blank"
            rel="noopener noreferrer"
            className="connection-badge"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
              <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
            </svg>
            Docs
          </a>
          <Link to="/story/branching" className="connection-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="6" y1="3" x2="6" y2="15" />
              <circle cx="18" cy="6" r="3" />
              <circle cx="6" cy="18" r="3" />
              <path d="M18 9a9 9 0 01-9 9" />
            </svg>
            Branching Guide
          </Link>
          <Link to="/settings" className="connection-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
            Settings
          </Link>
          <ThemeToggle />
          <button
            className="signout-btn"
            onClick={handleLogout}
            title="Sign out"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main className="dashboard-main">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Projects</h1>
            <p className="dashboard-subtitle">
              {isLoading
                ? "Loading..."
                : `${projects?.length ?? 0} project${projects?.length !== 1 ? "s" : ""}`}
              {projects &&
                projects.length > 0 &&
                ` · Last active ${timeAgo(projects[0]!.created_at)}`}
            </p>
          </div>

          <button
            className="new-project-btn"
            disabled={createMut.isPending}
            onClick={handleCreateProject}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {createMut.isPending ? "Creating…" : "New Project"}
          </button>
        </div>

        {/* Adaptive sampling entry point */}
        <Link to="/campaigns/new" className="flow360-banner" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="flow360-banner-icon" style={{ background: "rgba(255, 64, 128, 0.12)" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff4080" strokeWidth="1.5">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="5" cy="5" r="1" fill="#ff4080" />
                <circle cx="19" cy="6" r="1" fill="#ff4080" />
                <circle cx="6" cy="18" r="1" fill="#ff4080" />
                <circle cx="18" cy="19" r="1" fill="#ff4080" />
              </svg>
            </div>
            <div>
              <div className="flow360-banner-title">New adaptive sampling campaign</div>
              <div className="flow360-banner-subtitle">
                Budget-aware sampler for aerodynamic databases. Validated against NASA X-57 and Cessna 182 oracles.
              </div>
            </div>
          </div>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#52525b" strokeWidth="2" strokeLinecap="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>

        {/* Flow360 import banner */}
        <Link to="/flow360" className="flow360-banner">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="flow360-banner-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#818cf8"
                strokeWidth="1.5"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <div>
              <div className="flow360-banner-title">
                Import from Flow360
              </div>
              <div className="flow360-banner-subtitle">
                Browse geometries, meshes, and cases from your connected account
              </div>
            </div>
          </div>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#52525b"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>

        <CampaignList />

        {/* Loading */}
        {isLoading && <div className="loading-text">Loading projects…</div>}

        {/* Project list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {projects?.map((p) => (
            <div
              key={p.id}
              className="project-card"
              onClick={() => navigate(`/project/${p.id}`)}
              onMouseEnter={() => setHoveredProject(p.id)}
              onMouseLeave={() => {
                setHoveredProject(null);
                setShowMenu(null);
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* Left: icon + name + date */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: 14 }}
                >
                  <div className="project-icon">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#71717a"
                      strokeWidth="1.5"
                    >
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </div>
                  <div>
                    <div className="project-name">{p.name}</div>
                    <div className="project-date">
                      <span>{formatDate(p.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Right: activity hint + menu + arrow */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  <span
                    className="project-time-ago"
                    style={{
                      opacity: hoveredProject === p.id ? 1 : 0,
                    }}
                  >
                    {timeAgo(p.created_at)}
                  </span>

                  {/* Three-dot menu */}
                  <div style={{ position: "relative" }}>
                    <button
                      className={`dot-menu-btn${showMenu === p.id ? " dot-menu-btn--active" : ""}`}
                      style={{
                        opacity: hoveredProject === p.id ? 1 : 0,
                        transition: "opacity 0.15s ease",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(showMenu === p.id ? null : p.id);
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="#71717a"
                      >
                        <circle cx="12" cy="5" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="19" r="1.5" />
                      </svg>
                    </button>

                    {showMenu === p.id && (
                      <div className="dropdown-menu">
                        <button
                          className="dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(null);
                            setEditingProject({ id: p.id, name: p.name });
                            setEditName(p.name);
                          }}
                        >
                          Rename
                        </button>
                        <div className="dropdown-divider" />
                        <button
                          className="dropdown-item dropdown-item--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(null);
                            setDeletingProject({ id: p.id, name: p.name });
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Arrow chevron */}
                  <svg
                    className="project-arrow"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{
                      opacity: hoveredProject === p.id ? 1 : 0.4,
                      transform:
                        hoveredProject === p.id
                          ? "translateX(2px)"
                          : "translateX(0)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {!isLoading && projects?.length === 0 && (
          <div className="empty-state">
            No projects yet. Import data from Flow360 above to get started.
          </div>
        )}

        {/* Keyboard shortcut hint */}
        {projects && projects.length > 0 && (
          <div className="keyboard-hint">
            Press <kbd>N</kbd> to create a new project
          </div>
        )}
      </main>

      {/* Rename dialog */}
      <Dialog.Root
        open={!!editingProject}
        onOpenChange={(open) => !open && setEditingProject(null)}
      >
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Rename project</Dialog.Title>
          <Flex direction="column" gap="3" mt="3">
            <TextField.Root
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Project name"
              onKeyDown={(e) => {
                if (e.key === "Enter" && editingProject && editName.trim()) {
                  renameMut.mutate({
                    id: editingProject.id,
                    name: editName.trim(),
                  });
                }
              }}
            />
            <Flex gap="3" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={() => {
                  if (editingProject && editName.trim()) {
                    renameMut.mutate({
                      id: editingProject.id,
                      name: editName.trim(),
                    });
                  }
                }}
                disabled={!editName.trim()}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete confirmation */}
      <AlertDialog.Root
        open={!!deletingProject}
        onOpenChange={(open) => !open && setDeletingProject(null)}
      >
        <AlertDialog.Content maxWidth="480px">
          <AlertDialog.Title>
            Delete project &ldquo;{deletingProject?.name}&rdquo;?
          </AlertDialog.Title>
          <AlertDialog.Description size="2">
            This removes the project and all its imported nodes, edges, and
            history from <strong>Thread only</strong>. Nothing is deleted in
            Flow360 — your geometries, meshes, and cases remain untouched there.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={() =>
                  deletingProject && deleteMut.mutate(deletingProject.id)
                }
              >
                Delete from Thread
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </div>
  );
}
